import React, { useEffect, useMemo, useState } from 'react';
import planningEchangesApi from '../../services/planning-echanges';
import { ICONE_CANAL, LIBELLE_CANAL, libelleQuand, ancienneteContact } from '../../utils/echanges';
import './DerniersContacts.css';

// Tableau des derniers contacts clients — tous les dossiers NON restitués.
//
// Tri par SILENCE décroissant, « jamais contacté » en tête : c'est la seule
// lecture utile. Un tri par date décroissante mettrait en haut les dossiers
// dont on vient justement de s'occuper.
//
// ⚠️ Fermé au technicien : le composant n'est pas monté pour lui, et le serveur
// refuse la route de toute façon.
const PERIODES = [
  { jours: 90, label: '90 jours' },
  { jours: 180, label: '6 mois' },
  { jours: 0, label: 'Tout' },
];

export default function DerniersContacts({ onOuvrirDossier }) {
  const [jours, setJours] = useState(90);
  const [data, setData] = useState({ dossiers: [], total: 0, plafond: 500 });
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    setChargement(true); setErreur('');
    planningEchangesApi.derniersContacts(jours)
      .then(r => { if (vivant) setData(r.data); })
      .catch(e => { if (vivant) setErreur(e.response?.data?.error || 'Chargement impossible'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [jours]);

  const lignes = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return data.dossiers;
    return data.dossiers.filter(d =>
      [d.client_nom, d.client_prenom, d.immatriculation, d.numero_or, d.statut_nom]
        .some(v => String(v || '').toLowerCase().includes(q)));
  }, [data.dossiers, recherche]);

  const jamais = data.dossiers.filter(d => !d.dernier_contact).length;

  return (
    <div className="dc">
      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        <select className="page-select" value={jours} onChange={(e) => setJours(Number(e.target.value))}>
          {PERIODES.map(p => <option key={p.jours} value={p.jours}>{p.label}</option>)}
        </select>
        <span className="dc-resume">
          {chargement ? 'Chargement…' : (
            <>
              <b>{data.total}</b> dossier{data.total > 1 ? 's' : ''} non restitué{data.total > 1 ? 's' : ''}
              {jamais > 0 && <> · <b className="dc-alerte">{jamais}</b> jamais contacté{jamais > 1 ? 's' : ''}</>}
            </>
          )}
        </span>
      </div>

      {/* Plafond annoncé en clair, comme dans Suivi Atelier : une liste tronquée
          en silence se lit comme une liste complète. */}
      {!chargement && data.total > data.dossiers.length && (
        <div className="dc-plafond">
          Les <b>{data.dossiers.length}</b> dossiers au silence le plus long, sur {data.total}.
          Réduisez la période ou affinez la recherche pour voir les autres.
        </div>
      )}

      {erreur && <div className="dc-erreur">{erreur}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Dernier contact</th>
              <th>Client</th>
              <th>Immat.</th>
              <th>N° OR</th>
              <th>Statut atelier</th>
              <th>Par quel canal</th>
              <th>Téléphone</th>
            </tr>
          </thead>
          <tbody>
            {!chargement && lignes.length === 0 && (
              <tr><td colSpan={7} className="dc-vide">Aucun dossier à afficher.</td></tr>
            )}
            {lignes.map(d => {
              const a = ancienneteContact(d.dernier_contact);
              return (
                <tr
                  key={d.cle}
                  className="dc-ligne"
                  onClick={() => onOuvrirDossier && onOuvrirDossier(d.intervention_id)}
                  title="Ouvrir le dossier sur ses échanges"
                >
                  <td>
                    <span className={`dc-pastille${a.alerte ? ' dc-pastille--alerte' : ''}`}>
                      {d.dernier_contact ? a.texte : 'Jamais contacté'}
                    </span>
                    {d.a_rappeler_le && (
                      <span className="dc-rappel" title="Rappel promis au client">
                        ⏰ {new Date(d.a_rappeler_le).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </td>
                  <td className="dc-client">{[d.client_nom, d.client_prenom].filter(Boolean).join(' ') || '—'}</td>
                  <td><span className="dc-immat">{d.immatriculation || '—'}</span></td>
                  <td className="dc-or">{d.numero_or || <span className="dc-sans">pas encore d'OR</span>}</td>
                  <td>
                    {d.statut_nom ? (
                      <span className="dc-statut" style={{ background: d.statut_couleur || undefined, color: d.statut_text_color || undefined }}>
                        {d.statut_nom}
                      </span>
                    ) : <span className="dc-sans">—</span>}
                  </td>
                  <td className="dc-canal">
                    {d.dernier_contact
                      ? <>{ICONE_CANAL[d.dernier_canal] || '💬'} {LIBELLE_CANAL[d.dernier_canal] || ''} {d.dernier_sens === 'entrant' ? '↙' : '↗'}
                          <span className="dc-par"> · {d.dernier_par || ''}</span>
                          <span className="dc-exact"> · {libelleQuand(d.dernier_contact)}</span></>
                      : <span className="dc-sans">—</span>}
                  </td>
                  <td>
                    {d.client_tel
                      ? <a className="dc-tel" href={`tel:${String(d.client_tel).replace(/\D/g, '')}`} onClick={(e) => e.stopPropagation()}>{d.client_tel}</a>
                      : <span className="dc-sans">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
